import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  FileText, 
  Printer, 
  Scan, 
  Share2, 
  ArrowRight, 
  Lock, 
  Search, 
  History,
  Cloud,
  Download,
  FolderOpen
} from 'lucide-react';
import { Link } from 'react-router-dom';

const EFaxPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 via-indigo-50/50 to-blue-100/70"></div>
        
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm text-blue-700 text-xs font-medium border border-blue-200/50">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                <span>Communication Suite</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Meet eFax, Your
                <span className="bg-gradient-to-r from-blue-900 via-blue-600 to-blue-400 bg-clip-text text-transparent"> Digital Faxing Solution</span>
              </h1>
              
              <p className="text-lg text-gray-600 leading-relaxed max-w-lg">
                Ditch the clunky machine and expensive toner. Send, receive, and manage faxes digitally from any device, fully integrated with your patient records.
              </p>
              
              <div className="flex flex-wrap gap-3 pt-2">
                <Link to="/book-demo">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 rounded-xl text-lg transition-all duration-300 hover:scale-105 shadow-lg shadow-blue-200">
                    Get a Demo
                  </Button>
                </Link>
                <Button variant="outline" className="px-8 py-6 rounded-xl text-lg border-2 hover:bg-gray-50">
                  See Pricing
                </Button>
              </div>
            </div>
            
            {/* Right: Visual */}
            <div className="relative h-[600px] w-full flex items-center justify-center">
                <div className="relative w-full max-w-lg">
                    {/* Main Interface */}
                    <div className="bg-white rounded-[2rem] shadow-2xl p-6 border border-slate-100 relative z-10">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
                             <div className="flex items-center gap-3">
                                 <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                                     <Printer className="w-6 h-6 text-purple-600" />
                                 </div>
                                 <div>
                                     <div className="font-bold text-slate-900">Fax Inbox</div>
                                     <div className="text-xs text-slate-500">2 New Documents</div>
                                 </div>
                             </div>
                             <Button size="sm" variant="outline" className="text-xs">
                                 + New Fax
                             </Button>
                        </div>

                        {/* Document List */}
                        <div className="space-y-3 mb-6">
                            <div className="bg-slate-50 p-4 rounded-xl border border-blue-200 shadow-sm relative overflow-hidden group hover:border-blue-400 transition-colors">
                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500"></div>
                                <div className="flex justify-between items-start mb-2">
                                    <div className="flex items-center gap-2">
                                        <FileText className="w-4 h-4 text-slate-500" />
                                        <span className="font-bold text-slate-800 text-sm">Referral_Form.pdf</span>
                                    </div>
                                    <span className="text-[10px] text-slate-400">Just now</span>
                                </div>
                                <div className="text-xs text-slate-500 pl-6 mb-3">From: Dr. Sarah Johnson (Oral Surgeon)</div>
                                <div className="pl-6 flex gap-2">
                                    <Button size="sm" className="h-7 text-xs bg-white text-slate-700 border border-slate-200 hover:bg-slate-50">
                                        View
                                    </Button>
                                    <Button size="sm" className="h-7 text-xs bg-blue-600 text-white hover:bg-blue-700 gap-1">
                                        <FolderOpen className="w-3 h-3" />
                                        Attach to Patient
                                    </Button>
                                </div>
                            </div>

                            <div className="bg-white p-4 rounded-xl border border-slate-100 hover:border-slate-300 transition-colors opacity-70">
                                <div className="flex justify-between items-start mb-2">
                                    <div className="flex items-center gap-2">
                                        <FileText className="w-4 h-4 text-slate-400" />
                                        <span className="font-bold text-slate-700 text-sm">Lab_Results_9921.pdf</span>
                                    </div>
                                    <span className="text-[10px] text-slate-400">2h ago</span>
                                </div>
                                <div className="text-xs text-slate-400 pl-6">From: Quest Diagnostics</div>
                            </div>
                        </div>

                        {/* Storage Status */}
                        <div className="bg-slate-50 rounded-xl p-3 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Cloud className="w-4 h-4 text-slate-400" />
                                <span className="text-xs font-bold text-slate-600">Cloud Storage</span>
                            </div>
                            <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                                <div className="w-1/3 h-full bg-blue-500 rounded-full"></div>
                            </div>
                        </div>
                    </div>

                    {/* Floating Badge */}
                    <div className="absolute -right-6 top-32 bg-white p-4 rounded-xl shadow-xl border border-slate-100 animate-float">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                <Lock className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <div className="text-xs text-slate-500">Security</div>
                                <div className="text-lg font-bold text-slate-900">HIPAA Ready</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Cards Section */}
      <section className="py-24 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Smart Document Management
            </h2>
            <p className="text-lg text-gray-600">
              It's not just faxing; it's an intelligent document workflow for your practice.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-blue-600 group-hover:scale-110 transition-transform">
                <Cloud className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Cloud Access</h3>
              <p className="text-gray-600">
                Access your faxes from anywhere. Secure, HIPAA-compliant storage means you never lose a document.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-purple-600 group-hover:scale-110 transition-transform">
                <Search className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">OCR & Search</h3>
              <p className="text-gray-600">
                We automatically scan incoming faxes so you can search for patient names, dates, or keywords instantly.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-green-600 group-hover:scale-110 transition-transform">
                <Share2 className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Direct-to-Chart</h3>
              <p className="text-gray-600">
                Drag and drop incoming faxes directly into patient charts in Asha PMS. No printing or scanning required.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Workflow Visualization */}
      <section className="py-24 px-6 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-600/20 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2"></div>

        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-blue-400 font-bold tracking-wider text-xs uppercase mb-4">
                <Printer className="w-4 h-4" />
                Digital Workflow
              </div>
              <h2 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
                How eFax Works
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                Seamlessly integrates into your existing workflow, replacing hardware with software.
              </p>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 font-bold">1</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Incoming Fax</h4>
                    <p className="text-slate-400 text-sm">Fax arrives digitally and alerts your team instantly.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0 font-bold">2</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Smart Routing</h4>
                    <p className="text-slate-400 text-sm">AI identifies if it's a referral, lab result, or EOB and routes it.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0 font-bold">3</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Quick Action</h4>
                    <p className="text-slate-400 text-sm">Sign, annotate, or reply directly from the viewer.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0 font-bold">4</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">File Away</h4>
                    <p className="text-slate-400 text-sm">Save directly to the patient's record with one click.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
               <div className="bg-slate-800/50 backdrop-blur-xl rounded-3xl p-8 border border-slate-700 shadow-2xl">
                  <div className="flex items-center justify-between mb-8">
                      <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                              <FileText className="w-5 h-5 text-purple-400" />
                          </div>
                          <div>
                              <div className="font-bold text-white">New Fax Received</div>
                              <div className="text-xs text-slate-400">From: Dr. Johnson (Oral Surgeon)</div>
                          </div>
                      </div>
                      <div className="text-xs font-mono text-slate-500">2 pages</div>
                  </div>

                  <div className="bg-white p-4 rounded-lg shadow-lg relative min-h-[200px]">
                      <div className="w-full h-2 bg-slate-100 mb-4"></div>
                      <div className="w-3/4 h-2 bg-slate-100 mb-4"></div>
                      <div className="w-1/2 h-2 bg-slate-100 mb-8"></div>
                      
                      <div className="w-full h-32 bg-slate-50 border-2 border-dashed border-slate-200 rounded flex items-center justify-center text-slate-400 text-xs">
                          Referral Document Preview
                      </div>

                      {/* Action Overlay */}
                      <div className="absolute bottom-4 right-4 flex gap-2">
                          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white h-8 text-xs">
                              Attach to Patient
                          </Button>
                      </div>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Stats */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-blue-50 p-8 rounded-3xl text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">$500+</div>
              <div className="text-gray-600 font-medium">Monthly Savings on Paper/Toner</div>
            </div>
            <div className="bg-indigo-50 p-8 rounded-3xl text-center">
              <div className="text-4xl font-bold text-indigo-600 mb-2">100%</div>
              <div className="text-gray-600 font-medium">Digital Searchability</div>
            </div>
            <div className="bg-purple-50 p-8 rounded-3xl text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">0</div>
              <div className="text-gray-600 font-medium">Lost Documents</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Go paperless today.
          </h2>
          <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
            Upgrade your communication infrastructure with secure, cloud-based faxing.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book-demo">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-6 rounded-full text-lg shadow-xl shadow-blue-200 hover:shadow-blue-300 hover:-translate-y-1 transition-all">
                Switch to eFax
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EFaxPage;
